import React, { useState } from 'react'
import styled from 'styled-components'
import { useQuery, UseQueryResult } from 'react-query'
import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'

import {
  Card as MuiCard,
  CardContent,
  Grid,
  Typography,
} from '@material-ui/core'
import { spacing } from '@material-ui/system'
import {
  Add as AddIcon,
  LocalShipping as LocalShippingIcon,
} from '@material-ui/icons'

import { useQueryPaginator, DetailTable, LinkedField, PaginatedTable, ValueField } from 'src/components/Table'
import { PalletDetailTable } from 'src/components/Table/detail/Pallet'
import { PalletDialog } from 'src/modules/supply_chain/components/PalletDialog'
import { BarcodedUnitListTable } from 'src/components/Table/list/BarcodedUnit'
import { DamagedListTable } from 'src/components/Table/list/Damaged'
import { EventListTable } from 'src/components/Table/list/Event'
import { FoundListTable } from 'src/components/Table/list/Found'
import { LostListTable } from 'src/components/Table/list/Lost'
import { UnitListTable } from 'src/components/Table/list/Unit'

import { skuit } from 'src/services/skuit'
import { Pallet, PalletASN } from 'src/api/models'

import { DetailPage } from 'src/components/Pages/DetailPage'
import { DetailTab } from 'src/components/Pages/Tab'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

const Card = styled(MuiCard)(spacing)

const Centered = styled.div`
  text-align: center;
`

interface ASNDetailsProps {
  asn: UseQueryResult<PalletASN>
}

const ASNDetails = (props: ASNDetailsProps) => {
  const { asn } = props
  return (
    <Card mb={6}>
      <CardContent>
        <Centered>
          <LocalShippingIcon />
          <Typography variant="h6" gutterBottom>
            Advanced Shipping Notice
          </Typography>
          <DetailTable
            obj={asn}
            fields={[
              { id: 'guid', label: 'SquID', renderer: LinkedField(ValueField) },
              { id: 'trackingNumber', label: 'Tracking Number', renderer: ValueField },
              { id: 'billOfLadingNumber', label: 'Bill of Lading Number', renderer: ValueField },
            ]}
            customFields={asn.data?.customFields}
          />
        </Centered>
      </CardContent>
    </Card>
  )
}

interface PathProps {
  guid: string
}

interface DefaultProps {
  pallet: UseQueryResult<Pallet>
}

const InventoryTab = (props: DefaultProps) => {
  const { pallet } = props
  const { guid } = useParams<PathProps>()

  const skusPaginator = useQueryPaginator('sku-list', skuit.client.skus.list, {
    params: { page_size: 5, pallets: guid },
  })
  const unitsPaginator = useQueryPaginator('unit-list', skuit.client.units.list, {
    params: { page_size: 5, containers: guid },
  })
  const barcodedPaginator = useQueryPaginator(
    'barcoded-unit-list',
    skuit.client.barcodedUnits.list,
    { params: { page_size: 5, containers: guid } }
  )

  return (
    <DetailTab>
      {!!pallet.data?.asn && (
        <Grid item xs={12} lg={6}>
          <ASNDetails asn={{ ...pallet, data: pallet.data.asn }} />
        </Grid>
      )}
      <PaginatedTable
        title="SKUs"
        fields={[{ id: 'sku', label: 'Name', renderer: ValueField }]}
        paginator={skusPaginator}
      />
      <UnitListTable
        paginator={unitsPaginator}
        fields={['productName', 'product', 'sku', 'batch', 'client', 'numUnits', 'dateReceived']}
      />
      <BarcodedUnitListTable
        paginator={barcodedPaginator}
        fields={['guid', 'client', 'product', 'path']}
      />
    </DetailTab>
  )
}

const DamagesTab = () => {
  const { guid } = useParams<PathProps>()

  const damagedPaginator = useQueryPaginator('damaged-list', skuit.sc.damaged.list, {
    params: { page_size: 5, containers: guid },
  })

  return (
    <DetailTab>
      <DamagedListTable
        paginator={damagedPaginator}
        fields={['guid', 'client', 'product', 'sku', 'batch']}
      />
    </DetailTab>
  )
}

const LostNFoundTab = () => {
  const { guid } = useParams<PathProps>()

  const lostPaginator = useQueryPaginator('lost-list', skuit.sc.lost.list, {
    params: { page_size: 5, containers: guid },
  })
  const foundPaginator = useQueryPaginator('found-list', skuit.sc.found.list, {
    params: { page_size: 5, containers: guid },
  })

  return (
    <DetailTab>
      <LostListTable
        paginator={lostPaginator}
        fields={['sku', 'client', 'product', 'batch', 'numUnits', 'dateReceived', 'path']}
      />
      <FoundListTable
        paginator={foundPaginator}
        fields={['sku', 'client', 'product', 'batch', 'numUnits', 'path']}
      />
    </DetailTab>
  )
}

const EventsTab = () => {
  const { guid } = useParams<PathProps>()

  const eventsPaginator = useQueryPaginator('event-list', skuit.sc.events.list, {
    params: { page_size: 5, references: guid },
  })

  return (
    <DetailTab>
      <EventListTable
        paginator={eventsPaginator}
        title="Pallet Events"
        fields={['type', 'data', 'by', 'on']}
      />
    </DetailTab>
  )
}

export const PalletPage = () => {
  const { guid } = useParams<PathProps>()
  const [createOpen, setCreateOpen] = useState(false)

  const pallet = useQuery(['pallet-details', guid], () => skuit.sc.pallets.retrieve(guid))
  const eventsPaginator = useQueryPaginator('event-list', skuit.sc.events.list, {
    params: { page_size: 5, references: guid },
  })

  const refresh = () => {
    pallet.refetch()
    eventsPaginator.refresh()
  }

  return (
    <>
      {createOpen && (
        <PalletDialog
          open={createOpen}
          handleClose={() => {
            setCreateOpen(false)
          }}
        />
      )}
      <DetailPage
        obj={pallet}
        title="Pallets"
        refresh={refresh}
        breadcrumbs={[
          { title: 'Dashboard', url: '/' },
          { title: 'Pallets', url: '/inventory/pallets' },
        ]}
        detailComponent={<PalletDetailTable obj={pallet} refresh={refresh} />}
        menuItems={[
          {
            Icon: AddIcon,
            actions: [{ title: 'New Pallet', onClick: () => setCreateOpen(true) }],
          },
        ]}
        tabs={['Inventory', 'Damages', 'Lost & Found', 'Events']}
      >
        <InventoryTab pallet={pallet} />
        <DamagesTab />
        <LostNFoundTab />
        <EventsTab />
      </DetailPage>
    </>
  )
}
